// Function index:
//   MountHexWorldDeps  — inputs needed to mount an overworld / hex interior.
//   mountHexWorld(deps) — mount the hex scene, bind click-to-move,
//       right-click hex history, and wire Phase B3 streaming.

import type { ChatMount } from '../chat.js';
import type { HexHistoryOverlay } from './hex-history-overlay.js';
import type { BannerHandle } from '../combat-banner.js';
import { mountHexScene, type SceneMount } from '../hex-scene.js';
import type { WS } from '../ws.js';
import { streamHexes, type BootstrapResponse } from '../api.js';

export interface MountHexWorldDeps {
  appEl: HTMLElement;
  partyId: number;
  boot: BootstrapResponse;
  ws: WS;
  chat: ChatMount;
  banner: BannerHandle;
  isPaused: () => boolean;
  selfCharId: number | null;
  hexHistory: HexHistoryOverlay;
}

export interface HexWorldMount {
  scene: SceneMount;
  destroy(): void;
}

export async function mountHexWorld(deps: MountHexWorldDeps): Promise<HexWorldMount> {
  const { appEl, partyId, boot, ws, chat, isPaused, selfCharId, hexHistory } = deps;
  const scene = await mountHexScene(appEl, {
    hexes: boot.hexes,
    discovered: boot.discovered,
    selfCharacterId: selfCharId,
    characters: [
      ...boot.party_characters.map((c) => ({
        id: c.id,
        name: c.name,
        q: c.current_q,
        r: c.current_r,
        isSelf: c.id === selfCharId,
      })),
      // Cross-party visibility: PCs from other parties in the same world,
      // rendered as muted "foreign" tokens that move live via character.moved.
      ...boot.other_characters.map((c) => ({
        id: c.id,
        name: c.name,
        q: c.current_q,
        r: c.current_r,
        isSelf: false,
        foreign: true,
      })),
    ],
    npcs: boot.npcs.map((n) => ({ id: n.id, name: n.name, q: n.q, r: n.r })),
    locations: boot.locations,
    roads: boot.roads.map((r) => ({ id: r.id, path: r.path })),
    startQ: boot.character?.current_q ?? 0,
    startR: boot.character?.current_r ?? 0,
  });

  scene.onHexClick((q, r) => {
    if (isPaused()) return;
    ws.send('player.move.intent', { q, r });
  });

  // (q,r) → hex id + name, for the right-click history overlay. Seeded
  // from bootstrap, extended by every streamed disc below.
  const hexIdByKey = new Map<string, number>();
  const hexNameByKey = new Map<string, string | null>();
  for (const h of boot.hexes) {
    hexIdByKey.set(`${h.q},${h.r}`, h.id);
    hexNameByKey.set(`${h.q},${h.r}`, h.name);
  }

  scene.onHexRightClick((q, r) => {
    const key = `${q},${r}`;
    const id = hexIdByKey.get(key);
    if (id === undefined) return; // discovered via event but never loaded — nothing to show
    hexHistory.open(boot.world.id, id, hexNameByKey.get(key) || `(${q}, ${r})`);
  });

  let streamInFlight = false;
  scene.onNeedHexes(async (q, r, radius) => {
    if (streamInFlight) return;
    streamInFlight = true;
    try {
      const res = await streamHexes(partyId, q, r, radius);
      scene.addHexes(res.hexes);
      for (const h of res.hexes) {
        hexIdByKey.set(`${h.q},${h.r}`, h.id);
        hexNameByKey.set(`${h.q},${h.r}`, h.name);
      }
      for (const n of res.npcs) scene.addNpc({ id: n.id, name: n.name, q: n.q, r: n.r });
    } catch (err) {
      chat.system(`hex stream failed: ${(err as Error).message}`);
    } finally {
      streamInFlight = false;
    }
  });

  return {
    scene,
    destroy: () => { scene.destroy(); },
  };
}
